import React, { useState, useEffect, useMemo, useRef } from 'react'
import { format, startOfDay, endOfDay, addDays } from 'date-fns'
import { pt } from 'date-fns/locale'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { getAgendamentos, getBloqueiosDoDia, getConfiguracoes, getProfissionais, createBloqueio, updateBloqueio, deleteBloqueio, getAgendamentosNoPeriodo } from '../../lib/supabase'
import { ordenarProfissionais } from '../../lib/profissionais'
import { CORES_BLOQUEIO, MOTIVOS_BLOQUEIO, motivosBloqueioDeConfig, DURACAO_MINIMA_BLOQUEIO, duracaoMinimaBloqueioDeConfig } from '../../lib/bloqueios'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { useToast } from '../../contexts/ToastContext'
import { ESTADOS } from '../../lib/estados'
import Loading from '../../components/Loading'
import SeletorMotivoBloqueio from '../../components/SeletorMotivoBloqueio'

const SLOT_MIN = 30
const PX_POR_MIN = 1.6
const AXIS_WIDTH = 42
const COL_MIN_DESKTOP = 130
const COL_MIN_MOBILE = 240
const MOBILE_BREAKPOINT = '(max-width: 768px)'
// Mesmo critério do menu lateral (ver src/styles/global.css) — usado só para
// saber qual é o "contentor de scroll" real da página (documento inteiro vs.
// .admin-content), não para mudar o layout das colunas.
const CHROME_COMPACTO = '(max-width: 768px), (max-height: 480px) and (pointer: coarse)'

// A grelha cobre o dia inteiro (00:00–24:00) para que marcações e bloqueios
// fora do horário de atendimento fiquem visíveis. O horário de atendimento
// (aberturaMin–fechoMin) é apenas destacado e serve de alvo do auto-scroll.
const GRID_INICIO_MIN = 0
const GRID_FIM_MIN = 24 * 60 // 1440

function horaParaMinutos(hhmm) {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

function minutosParaHora(min) {
  const h = Math.floor(min / 60).toString().padStart(2, '0')
  const m = (min % 60).toString().padStart(2, '0')
  return `${h}:${m}`
}

function iniciais(nome) {
  return nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0].toUpperCase())
    .join('')
}

function corContraste(hex) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const luminancia = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminancia > 0.5 ? '#2C1A0E' : '#FFFFFF'
}


export default function AgendaDia() {
  const { tenant } = useTenant()
  const { perfil, podeFazer, estadosVisiveisAgenda } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const meuProfId = perfil?.profissional_id
  // Sem permissão para ver a agenda de todos → trancado à própria.
  const trancadoAMinha = !podeFazer('ver_agenda_todos') && !!meuProfId
  const podeGerirBloqueios = podeFazer('gerir_bloqueios')
  const podeBloquearTodos = podeFazer('bloqueios_todos')
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(searchParams.get('data') || format(new Date(), 'yyyy-MM-dd'))
  const [agendamentos, setAgendamentos] = useState([])
  const [bloqueios, setBloqueios] = useState([])
  // Modo bloqueio: clicar (ou arrastar sobre vários slots) na grelha abre um
  // modal rápido de bloqueio em vez de ir para Nova Marcação. arrastoRef guarda
  // o arrasto em curso (não gera re-render a cada movimento); arrastoVisual só
  // existe para desenhar o realce do intervalo selecionado.
  const [modoBloqueio, setModoBloqueio] = useState(false)
  const [slotBloqueio, setSlotBloqueio] = useState(null) // { prof, horaInicio, horaFim } | null
  const [bloqueioSelecionado, setBloqueioSelecionado] = useState(null) // bloqueio a editar/eliminar, em modo Bloqueios
  const arrastoRef = useRef(null) // { colKey, prof, inicioMin } | null
  const [arrastoVisual, setArrastoVisual] = useState(null) // { colKey, inicioMin, atualMin } | null
  const [profissionais, setProfissionais] = useState([])
  const [profsSelecionados, setProfsSelecionados] = useState(null) // null = todos
  const [profissionaisAbertos, setProfissionaisAbertos] = useState(false)
  const filtroProfissionaisRef = useRef(null)
  const [config, setConfig] = useState(null)
  const [carregando, setCarregando] = useState(true)
  const scrollRef = useRef(null)
  const legendaRef = useRef(null)

  // Em telemóvel mostra-se só um profissional de cada vez, a ocupar o ecrã
  // todo, com scroll horizontal (com "snap") para passar ao seguinte — em
  // vez de espremer todas as colunas ilegíveis lado a lado.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_BREAKPOINT).matches
  )
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_BREAKPOINT)
    const handler = e => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // "Chrome compacto" (mesmo critério do menu lateral) — cobre também o
  // telemóvel em paisagem, que "isMobile" (só largura) não apanha. Usado só
  // para encolher texto/paddings do cabeçalho e legenda; NÃO controla o
  // layout das colunas (isso continua a ser só "isMobile").
  const [chromeCompacto, setChromeCompacto] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(CHROME_COMPACTO).matches
  )
  useEffect(() => {
    const mq = window.matchMedia(CHROME_COMPACTO)
    const handler = e => setChromeCompacto(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // O filtro de profissionais é um dropdown compacto (em vez de um painel a
  // ocupar sempre a largura toda) — fecha ao clicar fora dele.
  useEffect(() => {
    if (!profissionaisAbertos) return
    function aoClicarFora(e) {
      if (filtroProfissionaisRef.current && !filtroProfissionaisRef.current.contains(e.target)) {
        setProfissionaisAbertos(false)
      }
    }
    document.addEventListener('mousedown', aoClicarFora)
    return () => document.removeEventListener('mousedown', aoClicarFora)
  }, [profissionaisAbertos])

  // Fim do arrasto de seleção de bloqueio — ouvido na window (não só no slot)
  // para funcionar mesmo que o rato solte fora da célula onde começou. Guarda
  // só os minutos em bruto (inicioMin/fimMin) — a hora de fim "inteligente"
  // (quando não houve arrasto real, ex.: toque num telemóvel) só é calculada
  // no render, ali onde "agendamentos"/"fechoMin" estão sempre atualizados
  // (aqui dentro do efeito, que só corre uma vez, ficariam desatualizados).
  useEffect(() => {
    function aoSoltarRato() {
      const emCurso = arrastoRef.current
      arrastoRef.current = null
      document.body.style.userSelect = ''
      if (!emCurso) return
      setArrastoVisual(atual => {
        if (!atual) return null
        const foiArrasto = atual.atualMin !== atual.inicioMin
        const inicioMin = Math.min(atual.inicioMin, atual.atualMin)
        const fimMin = foiArrasto
          ? Math.min(Math.max(atual.inicioMin, atual.atualMin) + SLOT_MIN, 23 * 60 + 59)
          : null // null = sem arrasto; hora de fim calculada ao abrir o modal
        setSlotBloqueio({ prof: emCurso.prof, inicioMin, fimMin })
        return null
      })
    }
    window.addEventListener('mouseup', aoSoltarRato)
    return () => window.removeEventListener('mouseup', aoSoltarRato)
  }, [])

  // Largura real da área de scroll (exclui a scrollbar), para dimensionar a
  // coluna única do profissional em telemóvel ao tamanho exato do ecrã.
  const [larguraScroll, setLarguraScroll] = useState(0)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const obs = new ResizeObserver(entries => {
      const entry = entries[0]
      if (entry) setLarguraScroll(entry.contentRect.width)
    })
    obs.observe(el)
    return () => obs.disconnect()
  }, [carregando])

  // Altura máxima da grelha — calculada a partir do espaço vertical que
  // realmente sobra até ao fundo do ecrã, em vez de um valor fixo. Um valor
  // fixo (ex.: "100vh - 280px") deixa quase nada visível em ecrãs baixos
  // (telemóvel em modo paisagem) e desperdiça espaço em ecrãs altos.
  //
  // Mede-se directamente o que sobra: altura visível do contentor de scroll
  // (".admin-content" no desktop, a janela inteira em modo compacto) menos o
  // que vem ANTES da grelha (topo) e DEPOIS dela (a legenda, via ref — sem
  // isto a legenda ficava escondida e sobrava sempre uma faixa vazia).
  //
  // Nota: NÃO se pode usar "scrollHeight vs. clientHeight" do contentor para
  // isto — scrollHeight nunca é menor que clientHeight, por isso quando o
  // conteúdo é mais curto do que o ecrã essa comparação dá sempre 0 e nunca
  // deteta que há espaço a mais para a grelha crescer (só deteta overflow).
  const [alturaMaxima, setAlturaMaxima] = useState(null)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    function recalcular() {
      const compacto = window.matchMedia(CHROME_COMPACTO).matches
      const contentor = compacto ? null : document.querySelector('.admin-content')
      const alturaVisivel = compacto ? window.innerHeight : (contentor?.clientHeight ?? window.innerHeight)
      const topoContentor = compacto ? 0 : (contentor?.getBoundingClientRect().top ?? 0)
      const topoScroll = el.getBoundingClientRect().top - topoContentor
      const alturaLegenda = legendaRef.current?.getBoundingClientRect().height ?? 0
      const margemInferior = 8
      // Mínimo baixo de propósito: em ecrãs muito baixos (telemóvel em
      // paisagem) o espaço realmente disponível pode ser só isto mesmo — um
      // mínimo alto forçaria sempre overflow/scroll, reabrindo a mesma folga
      // vazia que este cálculo serve para eliminar.
      setAlturaMaxima(Math.max(80, alturaVisivel - topoScroll - alturaLegenda - margemInferior))
    }
    recalcular()
    window.addEventListener('resize', recalcular)
    window.addEventListener('orientationchange', recalcular)
    return () => {
      window.removeEventListener('resize', recalcular)
      window.removeEventListener('orientationchange', recalcular)
    }
  }, [carregando, profissionaisAbertos])

  useEffect(() => {
    const d = searchParams.get('data')
    if (d && d !== data) setData(d)
  }, [searchParams, data])

  // Carregar configurações e profissionais uma vez
  useEffect(() => {
    if (!tenant) return
    Promise.all([
      getConfiguracoes(tenant.id),
      getProfissionais(tenant.id),
    ]).then(([cfg, profs]) => {
      setConfig(cfg)
      setProfissionais(ordenarProfissionais(profs, cfg.ordem_profissionais))
      // Sem permissão para ver todos (profissional trancado à própria agenda) →
      // mostra só o próprio. Caso contrário (ex.: manager, mesmo estando ligado
      // a um profissional) mostra todos ao abrir.
      if (trancadoAMinha) {
        setProfsSelecionados(new Set([meuProfId]))
      } else {
        setProfsSelecionados(new Set(profs.map(p => p.id)))
      }
    }).catch(() => {})
  }, [tenant, meuProfId, trancadoAMinha])

  // Carregar marcações do dia
  useEffect(() => {
    if (!tenant) return
    let ativo = true
    setCarregando(true)

    const timeout = setTimeout(() => {
      if (!ativo) return
      toast.erro('Não foi possível carregar as marcações (tempo esgotado). Verifica a ligação e tenta novamente.')
      setCarregando(false)
    }, 15000)

    const d = new Date(data + 'T12:00:00')
    const inicio = startOfDay(d).toISOString()
    const fim = endOfDay(d).toISOString()
    Promise.all([
      getAgendamentos(tenant.id, inicio, fim, { incluirCancelados: true }),
      getBloqueiosDoDia(tenant.id, inicio, fim),
    ])
      .then(([ags, bloqs]) => {
        if (!ativo) return
        setAgendamentos(ags)
        setBloqueios(bloqs)
      })
      .catch(e => { if (ativo) toast.erro(e.message) })
      .finally(() => {
        if (!ativo) return
        clearTimeout(timeout)
        setCarregando(false)
      })

    return () => { ativo = false; clearTimeout(timeout) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tenant])

  function mudarDia(delta) {
    const d = new Date(data + 'T12:00:00')
    const novaData = format(addDays(d, delta), 'yyyy-MM-dd')
    setData(novaData)
    setSearchParams({ data: novaData }, { replace: true })
  }

  function irHoje() {
    const hoje = format(new Date(), 'yyyy-MM-dd')
    setData(hoje)
    setSearchParams({ data: hoje }, { replace: true })
  }

  function toggleProf(id) {
    setProfsSelecionados(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        if (next.size === 1) return next // não permite desselecionar todos
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function selecionarTodos() {
    setProfsSelecionados(new Set(profissionais.map(p => p.id)))
  }

  const todosAtivos = profsSelecionados && profissionais.length > 0 && profsSelecionados.size === profissionais.length

  // Em ecrãs baixos (telemóvel em paisagem) a versão por extenso não cabe
  // numa linha sem empurrar a grelha para baixo — usa-se uma forma curta.
  const dataLabel = format(
    new Date(data + 'T12:00:00'),
    chromeCompacto ? "EEE, d MMM" : "EEEE, d 'de' MMMM 'de' yyyy",
    { locale: pt }
  )

  // Profissionais visíveis (em ordem: selecionados primeiro pelo filtro)
  const profissionaisVisiveis = useMemo(() => {
    if (!profsSelecionados) return profissionais
    return profissionais.filter(p => profsSelecionados.has(p.id))
  }, [profissionais, profsSelecionados])

  // Colunas da grelha (cabeçalho + corpo partilham o mesmo template para
  // ficarem sempre alinhados). Em telemóvel cada coluna ocupa a largura do
  // ecrã inteira (um profissional de cada vez, com scroll horizontal para
  // trocar); no desktop esticam com 1fr para preencher o espaço disponível.
  const larguraColunaMobile = Math.max(COL_MIN_MOBILE, larguraScroll - AXIS_WIDTH)
  const templateColunas = useMemo(() => {
    if (profissionaisVisiveis.length === 0) return `${AXIS_WIDTH}px 1fr`
    if (isMobile) return `${AXIS_WIDTH}px repeat(${profissionaisVisiveis.length}, ${larguraColunaMobile}px)`
    return `${AXIS_WIDTH}px repeat(${profissionaisVisiveis.length}, minmax(${COL_MIN_DESKTOP}px, 1fr))`
  }, [profissionaisVisiveis.length, isMobile, larguraColunaMobile])

  // Marcações filtradas (por profissional selecionado e por estado visível ao perfil)
  const agendamentosVisiveis = useMemo(() => {
    const porEstado = agendamentos.filter(a => estadosVisiveisAgenda.has(a.estado))
    if (!profsSelecionados) return porEstado
    return porEstado.filter(a => profsSelecionados.has(a.profissional_id))
  }, [agendamentos, profsSelecionados, estadosVisiveisAgenda])

  // Bloqueios filtrados — globais (profissional_id null) aparecem em todas as colunas
  const bloqueiosVisiveis = useMemo(() => {
    if (!profsSelecionados) return bloqueios
    return bloqueios.filter(b => b.profissional_id === null || profsSelecionados.has(b.profissional_id))
  }, [bloqueios, profsSelecionados])

  // Configurações de horário
  const { aberturaMin, fechoMin, almocoAtivoMin, almocoInicioMin, almocoFimMin } = useMemo(() => {
    if (!config) return { aberturaMin: 540, fechoMin: 1080, almocoAtivoMin: false, almocoInicioMin: 780, almocoFimMin: 840 }
    return {
      aberturaMin: horaParaMinutos(config.hora_abertura || '09:00'),
      fechoMin: horaParaMinutos(config.hora_fecho || '18:00'),
      almocoAtivoMin: config.almoco_ativo === 'true',
      almocoInicioMin: horaParaMinutos(config.hora_almoco_inicio || '13:00'),
      almocoFimMin: horaParaMinutos(config.hora_almoco_fim || '14:00'),
    }
  }, [config])

  // Lista de motivos de bloqueio configurável por tenant (Configurações).
  const motivosBloqueio = useMemo(() => (config ? motivosBloqueioDeConfig(config) : MOTIVOS_BLOQUEIO), [config])

  // Duração mínima de um bloqueio (minutos), configurável por tenant (Configurações).
  const duracaoMinimaBloqueio = useMemo(() => (config ? duracaoMinimaBloqueioDeConfig(config) : DURACAO_MINIMA_BLOQUEIO), [config])

  // Hora de fim sugerida para um bloqueio de um único slot (clique/toque sem
  // arrasto — sobretudo relevante em telemóvel, que não suporta arrasto): vai
  // até à próxima marcação da profissional (ou do fecho do espaço, se não
  // houver nenhuma a seguir); continua sempre editável no modal.
  function fimSugeridoMin(profId, inicioMin) {
    let proximo = null
    for (const a of agendamentos) {
      if (profId && a.profissional_id !== profId) continue
      const inicio = new Date(a.data_hora_inicio)
      const inicioAgMin = inicio.getHours() * 60 + inicio.getMinutes()
      if (inicioAgMin > inicioMin && (proximo === null || inicioAgMin < proximo)) proximo = inicioAgMin
    }
    if (proximo !== null) return proximo
    if (inicioMin < fechoMin) return fechoMin
    return Math.min(inicioMin + 60, 23 * 60 + 59)
  }

  // Hora de início ajustada: um slot da grelha é sempre de 30min, mas uma
  // marcação pode não estar alinhada a essa grelha (ex.: termina às 12:15) —
  // clicar nesse slot não deve sugerir bloquear a partir do início do slot
  // (12:00, ainda dentro da marcação), mas sim a partir do fim real dela.
  function inicioAjustadoMin(profId, min) {
    let fimMax = min
    for (const a of agendamentos) {
      if (profId && a.profissional_id !== profId) continue
      const inicio = new Date(a.data_hora_inicio)
      const fim = new Date(a.data_hora_fim)
      const inicioAgMin = inicio.getHours() * 60 + inicio.getMinutes()
      const fimAgMin = fim.getHours() * 60 + fim.getMinutes()
      if (inicioAgMin <= min && fimAgMin > min && fimAgMin > fimMax) fimMax = fimAgMin
    }
    return fimMax
  }

  // Trunca o fim de um arrasto se uma marcação começar a meio do intervalo
  // arrastado (ex.: arrastou até às 14:30 mas há uma marcação às 14:15) — o
  // bloqueio não deve invadir essa marcação, mesmo que ela esteja "no meio"
  // do último slot arrastado.
  function fimTruncadoPorMarcacao(profId, inicioMin, fimCandidatoMin) {
    let fim = fimCandidatoMin
    for (const a of agendamentos) {
      if (profId && a.profissional_id !== profId) continue
      const inicio = new Date(a.data_hora_inicio)
      const inicioAgMin = inicio.getHours() * 60 + inicio.getMinutes()
      if (inicioAgMin > inicioMin && inicioAgMin < fim) fim = inicioAgMin
    }
    return fim
  }

  const totalMinutos = GRID_FIM_MIN - GRID_INICIO_MIN
  const alturaTotal = totalMinutos * PX_POR_MIN

  // Linhas de hora para o eixo esquerdo (de hora a hora, dia inteiro)
  const linhasHora = useMemo(() => {
    const linhas = []
    for (let m = GRID_INICIO_MIN; m <= GRID_FIM_MIN; m += 60) {
      linhas.push({ min: m, label: minutosParaHora(m % 1440) })
    }
    return linhas
  }, [])

  // Linhas de slot (30min) para as células internas
  const linhasSlot = useMemo(() => {
    const linhas = []
    for (let m = GRID_INICIO_MIN; m < GRID_FIM_MIN; m += SLOT_MIN) {
      linhas.push(m)
    }
    return linhas
  }, [])

  // Converter agendamento para posição visual
  function agendamentoParaPosicao(ag) {
    const inicio = new Date(ag.data_hora_inicio)
    const fim = new Date(ag.data_hora_fim)
    const inicioMin = inicio.getHours() * 60 + inicio.getMinutes()
    const fimMin = fim.getHours() * 60 + fim.getMinutes()
    const top = (inicioMin - GRID_INICIO_MIN) * PX_POR_MIN
    const height = Math.max((fimMin - inicioMin) * PX_POR_MIN, 18)
    return { top, height, inicioMin, fimMin }
  }

  // Distribui as marcações de uma coluna lado a lado quando os horários se
  // sobrepõem (em vez de ficarem empilhadas na mesma posição, ilegíveis).
  // Agrupa em clusters de marcações mutuamente sobrepostas e atribui a cada
  // uma um índice de coluna dentro desse cluster (mesma técnica do Google
  // Calendar/Outlook: greedy — reutiliza a primeira coluna livre).
  function layoutComSobreposicao(agendamentos) {
    const itens = agendamentos
      .map(ag => ({ ag, ...agendamentoParaPosicao(ag) }))
      .sort((a, b) => a.inicioMin - b.inicioMin || a.fimMin - b.fimMin)

    const resultado = []
    let colunas = [] // fim (min) da última marcação em cada coluna do cluster atual
    let cluster = []
    let clusterFimMax = -Infinity

    function fecharCluster() {
      if (cluster.length === 0) return
      const totalCols = colunas.length
      for (const item of cluster) resultado.push({ ...item, totalCols })
      cluster = []
      colunas = []
    }

    for (const item of itens) {
      if (item.inicioMin >= clusterFimMax) {
        fecharCluster()
        clusterFimMax = -Infinity
      }
      let colIndex = colunas.findIndex(fim => fim <= item.inicioMin)
      if (colIndex === -1) {
        colIndex = colunas.length
        colunas.push(item.fimMin)
      } else {
        colunas[colIndex] = item.fimMin
      }
      cluster.push({ ...item, colIndex })
      clusterFimMax = Math.max(clusterFimMax, item.fimMin)
    }
    fecharCluster()

    return resultado
  }

  // Geometria horizontal (left/width) de uma marcação dentro do seu cluster
  // de sobreposição — PAD é a margem externa da coluna, GAP o espaço entre
  // marcações lado a lado.
  function geometriaXAgendamento(colIndex, totalCols) {
    const PAD = 4
    const GAP = 3
    if (totalCols <= 1) return { left: `${PAD}px`, width: `calc(100% - ${PAD * 2}px)` }
    const largura = `calc((100% - ${PAD * 2}px - ${(totalCols - 1) * GAP}px) / ${totalCols})`
    const esquerda = `calc(${PAD}px + ${colIndex} * ((100% - ${PAD * 2}px - ${(totalCols - 1) * GAP}px) / ${totalCols} + ${GAP}px))`
    return { left: esquerda, width: largura }
  }

  // Converter bloqueio para posição visual. A grelha cobre o dia inteiro, por
  // isso já não há clamp ao horário do espaço — os bloqueios mostram-se onde
  // realmente ocorrem (incluindo madrugada/noite).
  function bloqueioParaPosicao(b) {
    const inicio = new Date(b.data_hora_inicio)
    const fim = new Date(b.data_hora_fim)
    const inicioMin = inicio.getHours() * 60 + inicio.getMinutes()
    const rawFimMin = fim.getHours() * 60 + fim.getMinutes()
    // Se o fim cai à meia-noite (0 min) considera o fim do dia (24:00).
    const fimMin = rawFimMin === 0 ? GRID_FIM_MIN : rawFimMin
    const top = (inicioMin - GRID_INICIO_MIN) * PX_POR_MIN
    const height = Math.max((fimMin - inicioMin) * PX_POR_MIN, 20)
    return { top, height, inicioMin, fimMin }
  }

  // Hora atual (para marcador)
  const agora = new Date()
  const agoraMin = agora.getHours() * 60 + agora.getMinutes()
  const agoraEHoje = format(agora, 'yyyy-MM-dd') === data
  // A grelha cobre o dia inteiro, por isso o marcador mostra-se sempre que é hoje.
  const agoraDentroHorario = agoraEHoje
  const agoraTop = (agoraMin - GRID_INICIO_MIN) * PX_POR_MIN

  const totalVisiveis = agendamentosVisiveis.length
  const totalBloqueios = bloqueiosVisiveis.length

  // Ao abrir (ou mudar de dia), a grelha cobre o dia inteiro mas o scroll foca
  // no horário de atendimento — deixa uma pequena margem antes da abertura.
  useEffect(() => {
    if (carregando || !config) return
    const el = scrollRef.current
    if (!el) return
    const margemMin = 30
    const alvo = Math.max(0, (aberturaMin - GRID_INICIO_MIN - margemMin) * PX_POR_MIN)
    el.scrollTop = alvo
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregando, config, aberturaMin, data])

  // Chips de seleção de profissionais — partilhados entre o painel mobile
  // (bloco fixo) e o dropdown desktop (popover), para não duplicar a lógica.
  function listaChipsProfissionais() {
    return (
      <>
        {profissionais.map(prof => {
          const ativo = profsSelecionados?.has(prof.id)
          const cor = prof.cor_agenda || '#8B4A2F'
          return (
            <button
              key={prof.id}
              onClick={() => toggleProf(prof.id)}
              title={prof.nome}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '7px',
                padding: '5px 12px 5px 6px',
                borderRadius: '20px', border: '2px solid',
                borderColor: ativo ? cor : 'var(--cor-borda)',
                background: ativo ? cor + '18' : 'transparent',
                color: ativo ? cor : 'var(--cor-texto-suave)',
                fontSize: '13px', fontWeight: '600',
                cursor: 'pointer', transition: 'all 0.15s',
                fontFamily: 'var(--font-ui)', whiteSpace: 'nowrap',
              }}
            >
              <span style={{
                width: '22px', height: '22px', borderRadius: '50%',
                background: ativo ? cor : 'var(--cor-borda)',
                color: ativo ? corContraste(cor) : 'var(--cor-texto-suave)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '10px', fontWeight: '700', flexShrink: 0,
                transition: 'all 0.15s',
              }}>
                {iniciais(prof.nome)}
              </span>
              {prof.nome.split(' ')[0]}
            </button>
          )
        })}
        {!todosAtivos && (
          <button
            onClick={selecionarTodos}
            style={{
              fontSize: '12px', color: 'var(--cor-texto-suave)',
              background: 'none', border: 'none', cursor: 'pointer',
              textDecoration: 'underline', padding: '4px 6px',
              fontFamily: 'var(--font-ui)',
            }}
          >
            Todos
          </button>
        )}
      </>
    )
  }

  const resumoProfissionais = todosAtivos ? 'todos' : `${profsSelecionados?.size || 0} de ${profissionais.length}`

  return (
    <main style={{ width: '100%', padding: chromeCompacto ? '10px clamp(12px, 2vw, 32px) 10px' : '16px clamp(16px, 2vw, 32px) 16px' }}>

      {/* Cabeçalho — tudo numa única linha (quebra em ecrãs estreitos) para
          deixar o máximo de altura possível para a agenda: título + nova +
          data à esquerda; profissionais (dropdown compacto, só ocupa espaço
          quando aberto) + navegação de data à direita. Mesmo desenho em
          todos os tamanhos de ecrã — é o que menos altura consome. */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center',
        justifyContent: 'space-between', gap: chromeCompacto ? '6px 14px' : '10px 20px',
        marginBottom: chromeCompacto ? '6px' : '10px',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: chromeCompacto ? '8px 10px' : '10px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: chromeCompacto ? '8px' : '12px' }}>
            <h2 style={{ fontSize: chromeCompacto ? '16px' : '20px', margin: 0 }}>Agenda do Dia</h2>
            <Link to={`/admin/nova-marcacao?data=${data}`} className="btn-primario" style={{
              textDecoration: 'none', padding: chromeCompacto ? '5px 10px' : '7px 14px', fontSize: '13px',
              minHeight: 'auto', display: 'inline-flex', alignItems: 'center',
            }}>
              + Nova
            </Link>
          </div>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--cor-texto-suave)', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
            {dataLabel}
            {' · '}
            <strong style={{ color: 'var(--cor-principal)' }}>
              {totalVisiveis} marca{totalVisiveis === 1 ? 'ção' : 'ções'}
            </strong>
            {totalBloqueios > 0 && (
              <>
                {' · '}
                <strong style={{ color: '#B45309' }}>
                  {totalBloqueios} bloqueio{totalBloqueios === 1 ? '' : 's'}
                </strong>
              </>
            )}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: chromeCompacto ? '4px' : '8px' }}>
          {/* Bloqueios + Profissionais agrupados sem quebra — em ecrãs estreitos
              a linha só pode quebrar ENTRE este grupo e o de navegação de data,
              nunca a meio de um grupo. Em modo compacto os botões ficam mais
              apertados (padding/tamanho de letra menores) e o "Hoje" some —
              tudo para tentar caber numa única linha num telemóvel. */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: chromeCompacto ? '4px' : '8px' }}>
            {podeGerirBloqueios && (
              <button
                onClick={() => setModoBloqueio(v => !v)}
                title="Clica num horário na grelha para criar um bloqueio"
                className={modoBloqueio ? 'btn-primario' : 'btn-secundario'}
                style={{
                  padding: chromeCompacto ? '4px 7px' : '8px 14px', fontSize: chromeCompacto ? '11.5px' : '13px',
                  whiteSpace: 'nowrap',
                  ...(modoBloqueio ? { background: '#B45309', borderColor: '#B45309' } : {}),
                }}
              >
                Bloqueios
              </button>
            )}
            {profissionais.length > 0 && !trancadoAMinha && (
              <div ref={filtroProfissionaisRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setProfissionaisAbertos(v => !v)}
                  aria-expanded={profissionaisAbertos}
                  className="btn-secundario"
                  style={{
                    padding: chromeCompacto ? '4px 7px' : '8px 14px', fontSize: chromeCompacto ? '11.5px' : '13px',
                    display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
                  }}
                >
                  Profissionais
                  {!todosAtivos && !chromeCompacto && <span style={{ fontWeight: '500' }}>{' — '}{resumoProfissionais}</span>}
                  <span style={{ fontSize: '10px' }}>{profissionaisAbertos ? '▲' : '▼'}</span>
                </button>
                {profissionaisAbertos && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)',
                    // Em telemóvel o botão pode ficar perto da margem esquerda
                    // (linha quebrada) — ancorar à direita empurraria o popover
                    // para fora do ecrã, por isso ancora-se à esquerda e a
                    // largura é limitada à largura do ecrã (com respiro).
                    left: isMobile ? 0 : 'auto', right: isMobile ? 'auto' : 0,
                    zIndex: 20,
                    background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)',
                    borderRadius: 'var(--raio-lg)', boxShadow: 'var(--sombra)',
                    padding: '12px', width: 'min(320px, calc(100vw - 32px))',
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px',
                  }}>
                    {listaChipsProfissionais()}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navegação de data — também agrupada sem quebra, para não separar
              "Hoje" do resto (ver nota acima). Em modo compacto o "Hoje" some
              de vez (cabe menos, e "hoje" já se vê destacado na grelha). */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: chromeCompacto ? '4px' : '8px' }}>
            <button onClick={() => mudarDia(-1)} className="btn-secundario" style={{ padding: chromeCompacto ? '4px 8px' : '8px 14px' }}>←</button>
            <input
              type="date" value={data}
              onChange={e => { setData(e.target.value); setSearchParams({ data: e.target.value }, { replace: true }) }}
              style={{
                padding: chromeCompacto ? '4px 4px' : '8px 12px', border: '1px solid var(--cor-borda)',
                borderRadius: 'var(--raio)', fontSize: chromeCompacto ? '12.5px' : '14px',
                fontFamily: 'var(--font-ui)', background: 'var(--cor-branco)',
              }}
            />
            <button onClick={() => mudarDia(1)} className="btn-secundario" style={{ padding: chromeCompacto ? '4px 8px' : '8px 14px' }}>→</button>
            {!chromeCompacto && (
              <button onClick={irHoje} className="btn-secundario" style={{ padding: '8px 14px', fontSize: '12px' }}>Hoje</button>
            )}
          </div>
        </div>
      </div>

      {isMobile && profissionaisVisiveis.length > 1 && !modoBloqueio && (
        <p style={{
          textAlign: 'center', fontSize: '11px', color: 'var(--cor-texto-suave)',
          margin: '0 0 4px',
        }}>
          ← Desliza para ver outro profissional →
        </p>
      )}

      {modoBloqueio && (
        <p style={{
          textAlign: 'center', fontSize: '12px', color: '#92500E',
          background: '#FFFBEB', border: '1px solid #F4C77B', borderRadius: 'var(--raio)',
          margin: '0 0 8px', padding: '6px 12px',
        }}>
          Clica num horário para bloquear, ou arrasta para selecionar vários horários seguidos.
        </p>
      )}

      {carregando ? <Loading /> : (
        <div style={{
          background: 'var(--cor-branco)',
          border: '1px solid var(--cor-borda)',
          borderRadius: 'var(--raio-lg)',
          overflow: 'hidden',
          boxShadow: 'var(--sombra)',
        }}>

          {/* Grade de horários — o cabeçalho e a grelha vivem no mesmo
              contentor de scroll e usam o mesmo template de colunas, por isso
              alinham sempre e acompanham-se no scroll horizontal. A grelha
              cobre o dia inteiro mas ao abrir foca no horário de atendimento.
              Em telemóvel ativa-se "scroll snap" horizontal para o scroll
              parar sempre alinhado num profissional (efeito de "página"). */}
          <div ref={scrollRef} style={{
            overflowX: 'auto', overflowY: 'auto',
            maxHeight: alturaMaxima ? `${alturaMaxima}px` : 'calc(100vh - 280px)',
            scrollSnapType: (isMobile && profissionaisVisiveis.length > 1) ? 'x mandatory' : 'none',
            // Compensa a largura do eixo de horas (sticky) para o snap alinhar
            // o início de cada coluna logo a seguir ao eixo, não por baixo dele.
            scrollPaddingLeft: `${AXIS_WIDTH}px`,
          }}>

            {/* Contentor com min-width: max-content para que o cabeçalho e a
                grelha ocupem toda a largura do scroll horizontal — assim o fundo
                creme do cabeçalho deixa de cortar a meio ao deslizar. */}
            <div style={{ minWidth: 'max-content' }}>

            {/* Cabeçalho das colunas de profissionais — sticky no topo (scroll
                vertical) e acompanha o scroll horizontal da grelha. */}
            {profissionaisVisiveis.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: templateColunas,
                borderBottom: '2px solid var(--cor-borda)',
                background: 'var(--cor-creme)',
                position: 'sticky', top: 0, zIndex: 10,
              }}>
                <div style={{ borderRight: '1px solid var(--cor-borda)', position: 'sticky', left: 0, zIndex: 2, background: 'var(--cor-creme)' }} />
                {profissionaisVisiveis.map((prof, i) => {
                  const cor = prof.cor_agenda || '#8B4A2F'
                  return (
                    <div key={prof.id} style={{
                      padding: '8px 4px',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                      borderRight: i < profissionaisVisiveis.length - 1 ? '1px solid var(--cor-borda)' : 'none',
                    }}>
                      <div style={{
                        width: '30px', height: '30px', borderRadius: '50%',
                        background: cor, color: corContraste(cor),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '11px', fontWeight: '700', flexShrink: 0,
                      }}>
                        {iniciais(prof.nome)}
                      </div>
                      <span style={{
                        fontSize: '11px', fontWeight: '600', color: 'var(--cor-texto)',
                        textAlign: 'center', lineHeight: 1.2,
                        maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {prof.nome.split(' ')[0]}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: templateColunas,
              position: 'relative',
            }}>

              {/* Linhas-fronteira do horário de atendimento (abertura e fecho),
                  sobre todas as colunas, para enquadrar visualmente o horário. */}
              <div style={{
                position: 'absolute', left: 0, right: 0, zIndex: 4, pointerEvents: 'none',
                top: `${(aberturaMin - GRID_INICIO_MIN) * PX_POR_MIN}px`,
                borderTop: '2px solid var(--cor-principal)', opacity: 0.5,
              }} />
              <div style={{
                position: 'absolute', left: 0, right: 0, zIndex: 4, pointerEvents: 'none',
                top: `${(fechoMin - GRID_INICIO_MIN) * PX_POR_MIN}px`,
                borderTop: '2px solid var(--cor-principal)', opacity: 0.5,
              }} />

              {/* Coluna do eixo temporal (horas) — sticky à esquerda para se
                  manter sempre visível durante o scroll horizontal entre
                  profissionais (sobretudo relevante em telemóvel). */}
              <div style={{
                position: 'sticky', left: 0, zIndex: 6,
                height: `${alturaTotal}px`, borderRight: '1px solid var(--cor-borda)',
                background: 'var(--cor-branco)', flexShrink: 0,
              }}>
                {linhasHora.map(({ min, label }) => {
                  const foraHorario = min < aberturaMin || min > fechoMin
                  return (
                    <div key={min} style={{
                      position: 'absolute',
                      top: `${(min - GRID_INICIO_MIN) * PX_POR_MIN - 7}px`,
                      left: 0, right: 0,
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <span style={{
                        fontSize: '10px',
                        color: 'var(--cor-texto-suave)',
                        opacity: foraHorario ? 0.45 : 1,
                        fontWeight: '600', lineHeight: 1,
                      }}>
                        {label}
                      </span>
                    </div>
                  )
                })}
              </div>

              {/* Colunas por profissional */}
              {(profissionaisVisiveis.length > 0 ? profissionaisVisiveis : [null]).map((prof, colIdx) => {
                const agsDaColuna = prof
                  ? agendamentosVisiveis.filter(a => a.profissional_id === prof.id)
                  : agendamentosVisiveis

                const bloqueiosDaColuna = bloqueiosVisiveis.filter(b =>
                  prof ? (b.profissional_id === prof.id || b.profissional_id === null) : true
                )

                // Usados tanto pelos slots vazios como pelas marcações (em modo Bloqueios,
                // clicar numa marcação também tenta abrir o bloqueio rápido).
                const colKey = prof?.id || '__all__'
                const semPermissaoNestaColuna = modoBloqueio && !podeBloquearTodos && prof?.id && prof.id !== meuProfId

                return (
                  <div key={prof?.id || 'all'} style={{
                    position: 'relative',
                    height: `${alturaTotal}px`,
                    borderRight: colIdx < (profissionaisVisiveis.length || 1) - 1 ? '1px solid var(--cor-borda)' : 'none',
                    scrollSnapAlign: isMobile ? 'start' : 'unset',
                    scrollSnapStop: isMobile ? 'always' : 'normal',
                  }}>

                    {/* Linhas de slot de fundo — clicáveis para nova marcação
                        (ou, em modo bloqueio, clicáveis/arrastáveis para abrir
                        o bloqueio rápido sobre um ou vários slots). */}
                    {linhasSlot.map(min => {
                      const hora = minutosParaHora(min)
                      const params = new URLSearchParams({ data, hora })
                      if (prof?.id) params.set('profissional', prof.id)
                      const emArrastoNestaColuna = arrastoVisual?.colKey === colKey
                      const noIntervaloArrastado = emArrastoNestaColuna
                        && min >= Math.min(arrastoVisual.inicioMin, arrastoVisual.atualMin)
                        && min <= Math.max(arrastoVisual.inicioMin, arrastoVisual.atualMin)
                      // Se este slot ainda tiver uma marcação em curso (ex.: marcação
                      // das 11:45 às 12:15, num slot que começa às 12:00), o bloqueio
                      // só deve começar quando ela terminar, não no início do slot.
                      const inicioClicMin = inicioAjustadoMin(prof?.id, min)

                      function abrirModalBloqueio() {
                        setSlotBloqueio({ prof, inicioMin: inicioClicMin, fimMin: null })
                      }
                      function aoClicar() {
                        if (!modoBloqueio) navigate(`/admin/nova-marcacao?${params}`)
                        // Em modo bloqueio, a abertura do modal é feita pelo
                        // mousedown+mouseup (aoRatoPousar/window "mouseup"), que
                        // cobre tanto um clique simples como um arrasto — aqui
                        // não faz nada para não abrir o modal em duplicado.
                      }
                      function aoRatoPousar(e) {
                        if (!modoBloqueio) return
                        if (semPermissaoNestaColuna) { toast.erro('Sem permissão para bloquear esta profissional.'); return }
                        e.preventDefault()
                        document.body.style.userSelect = 'none'
                        arrastoRef.current = { colKey, prof, inicioMin: inicioClicMin }
                        setArrastoVisual({ colKey, inicioMin: inicioClicMin, atualMin: inicioClicMin })
                      }
                      function aoRatoEntrar(e) {
                        if (modoBloqueio) {
                          e.currentTarget.style.background = semPermissaoNestaColuna ? '' : '#FEF3C7'
                        } else {
                          e.currentTarget.style.background = 'var(--cor-creme)'
                        }
                        if (arrastoRef.current?.colKey === colKey) {
                          setArrastoVisual(v => (v ? { ...v, atualMin: min } : v))
                        }
                      }
                      return (
                        <div
                          key={min}
                          role="button"
                          tabIndex={0}
                          title={modoBloqueio
                            ? (semPermissaoNestaColuna ? 'Sem permissão para bloquear esta profissional' : `Bloquear às ${minutosParaHora(inicioClicMin)}${prof ? ` — ${prof.nome.split(' ')[0]}` : ''} (arrasta para selecionar vários horários)`)
                            : `Nova marcação às ${hora}${prof ? ` — ${prof.nome.split(' ')[0]}` : ''}`
                          }
                          onClick={aoClicar}
                          onMouseDown={aoRatoPousar}
                          onKeyDown={e => { if (e.key === 'Enter') { if (modoBloqueio) { if (!semPermissaoNestaColuna) abrirModalBloqueio() } else navigate(`/admin/nova-marcacao?${params}`) } }}
                          style={{
                            position: 'absolute',
                            top: `${(min - GRID_INICIO_MIN) * PX_POR_MIN}px`,
                            left: 0, right: 0,
                            height: `${SLOT_MIN * PX_POR_MIN}px`,
                            // Fora do horário de atendimento: fundo ligeiramente
                            // esbatido para distinguir do horário do espaço.
                            background: noIntervaloArrastado
                              ? '#FCD34D'
                              : (min < aberturaMin || min >= fechoMin) ? '#FAF7F2' : 'transparent',
                            borderBottom: (min - GRID_INICIO_MIN) % 60 === 0
                              ? '1px solid var(--cor-borda)'
                              : '1px dashed #E8E0D5',
                            boxSizing: 'border-box',
                            cursor: semPermissaoNestaColuna ? 'not-allowed' : 'pointer',
                          }}
                          onMouseEnter={aoRatoEntrar}
                          onMouseLeave={e => { if (!noIntervaloArrastado) e.currentTarget.style.background = (min < aberturaMin || min >= fechoMin) ? '#FAF7F2' : 'transparent' }}
                        />
                      )
                    })}

                    {/* Bloco de almoço */}
                    {almocoAtivoMin && (
                      <div style={{
                        position: 'absolute',
                        top: `${(almocoInicioMin - GRID_INICIO_MIN) * PX_POR_MIN}px`,
                        left: 0, right: 0,
                        height: `${(almocoFimMin - almocoInicioMin) * PX_POR_MIN}px`,
                        background: 'repeating-linear-gradient(45deg, #F5F0E8, #F5F0E8 4px, #EAE3D6 4px, #EAE3D6 8px)',
                        opacity: 0.7,
                        zIndex: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        pointerEvents: 'none',
                      }}>
                        {(colIdx === 0 || isMobile) && (
                          <span style={{
                            fontSize: '10px', fontWeight: '700',
                            color: 'var(--cor-texto-suave)', letterSpacing: '0.08em',
                            textTransform: 'uppercase', opacity: 0.8,
                          }}>
                            Almoço
                          </span>
                        )}
                      </div>
                    )}

                    {/* Bloqueios */}
                    {bloqueiosDaColuna.map(b => {
                      const { top, height } = bloqueioParaPosicao(b)
                      const isGlobal = b.profissional_id === null
                      const isSmall = height < 32
                      // Cor personalizada do bloqueio; sem cor mantém o automático
                      // (âmbar para fecho do espaço, vermelho para profissional).
                      const corBase = b.cor || (isGlobal ? '#D97706' : '#EF4444')
                      const fundo = b.cor
                        ? `repeating-linear-gradient(135deg, ${corBase}22, ${corBase}22 5px, ${corBase}40 5px, ${corBase}40 10px)`
                        : (isGlobal
                            ? 'repeating-linear-gradient(135deg, #FEF3C7, #FEF3C7 5px, #FDE68A 5px, #FDE68A 10px)'
                            : 'repeating-linear-gradient(135deg, #FEE2E2, #FEE2E2 5px, #FECACA 5px, #FECACA 10px)')
                      const podeEditarEsteBloqueio = podeGerirBloqueios && (podeBloquearTodos || b.profissional_id === meuProfId)
                      function aoClicarBloqueio() {
                        if (!podeEditarEsteBloqueio) { toast.erro('Sem permissão para editar este bloqueio.'); return }
                        const diaInicio = format(new Date(b.data_hora_inicio), 'yyyy-MM-dd')
                        const diaFim = format(new Date(b.data_hora_fim), 'yyyy-MM-dd')
                        if (diaInicio !== data || diaFim !== data) {
                          toast.aviso('Este bloqueio abrange vários dias — edita-o na página Bloqueios.')
                          return
                        }
                        setBloqueioSelecionado(b)
                      }
                      return (
                        <div
                          key={b.id}
                          title={podeEditarEsteBloqueio ? `${b.motivo || 'Bloqueado'} (clica para editar)` : (b.motivo || 'Bloqueado')}
                          onClick={aoClicarBloqueio}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => { if (e.key === 'Enter') aoClicarBloqueio() }}
                          style={{
                            position: 'absolute',
                            top: `${top}px`,
                            left: '2px', right: '2px',
                            height: `${height - 1}px`,
                            background: fundo,
                            border: `1.5px solid ${corBase}99`,
                            borderLeft: `3px solid ${corBase}`,
                            borderRadius: '5px',
                            overflow: 'hidden',
                            zIndex: 2,
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: isSmall ? 'center' : 'flex-start',
                            padding: isSmall ? '0 6px' : '4px 6px',
                            gap: '4px',
                            pointerEvents: 'auto',
                            cursor: podeEditarEsteBloqueio ? 'pointer' : 'default',
                          }}
                        >
                          {!isSmall && (
                            <span style={{
                              fontSize: '10px', fontWeight: '700',
                              color: corBase,
                              letterSpacing: '0.02em',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {b.motivo || 'Bloqueado'}
                            </span>
                          )}
                        </div>
                      )
                    })}

                    {/* Marcador da hora atual */}
                    {agoraDentroHorario && (
                      <div style={{
                        position: 'absolute',
                        top: `${agoraTop}px`,
                        left: 0, right: 0,
                        zIndex: 5, pointerEvents: 'none',
                        display: 'flex', alignItems: 'center',
                      }}>
                        <div style={{
                          width: '8px', height: '8px', borderRadius: '50%',
                          background: '#DC2626', flexShrink: 0, marginLeft: '-4px',
                        }} />
                        <div style={{ flex: 1, height: '1.5px', background: '#DC2626', opacity: 0.7 }} />
                      </div>
                    )}

                    {/* Marcações — dispostas lado a lado quando os horários se sobrepõem */}
                    {layoutComSobreposicao(agsDaColuna).map(({ ag, top, height, colIndex, totalCols }) => {
                      const cor = ag.profissionais?.cor_agenda || '#8B4A2F'
                      const estadoCfg = ESTADOS[ag.estado] || { label: ag.estado, cor: '#6B7280' }
                      const corEstado = estadoCfg.cor
                      const isSmall = height < 55
                      const { left, width } = geometriaXAgendamento(colIndex, totalCols)
                      // borda esquerda: profissional em cima, estado em baixo (split visual)
                      const borderLeft = ag.estado === 'confirmado'
                        ? `3px solid ${cor}`
                        : `3px solid ${corEstado}`

                      const tooltipMarcacao = [
                        ag.cliente_nome || 'Sem nome',
                        estadoCfg.label,
                        ag.servicos?.nome || 'Sem tratamento',
                        `${format(new Date(ag.data_hora_inicio), 'HH:mm')} - ${format(new Date(ag.data_hora_fim), 'HH:mm')}`,
                        `Sala: ${ag.salas?.nome || 'Sem sala'}`,
                        `Tel: ${ag.cliente_telefone || ag.clientes?.telefone || 'Sem contacto'}`,
                      ].join('\n')

                      function aoClicarMarcacao() {
                        navigate(`/admin/agendamento/${ag.id}`)
                      }

                      return (
                        <div
                          key={ag.id}
                          onClick={aoClicarMarcacao}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => { if (e.key === 'Enter') aoClicarMarcacao() }}
                          title={tooltipMarcacao}
                          style={{
                            position: 'absolute',
                            top: `${top}px`,
                            left, width,
                            zIndex: 3 + colIndex,
                            height: `${height - 2}px`,
                            background: cor + '18',
                            border: `1.5px solid ${cor}44`,
                            borderLeft,
                            borderRadius: '6px',
                            padding: isSmall ? '2px 6px 2px 5px' : '5px 8px 5px 6px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          {isSmall ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontWeight: '700', fontSize: '11px', color: cor, flexShrink: 0 }}>
                                {format(new Date(ag.data_hora_inicio), 'HH:mm')}
                              </span>
                              <span style={{ fontSize: '11px', color: 'var(--cor-texto)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                {ag.cliente_nome || 'Sem nome'}
                              </span>
                              {ag.estado !== 'confirmado' && (
                                <span style={{
                                  width: '7px', height: '7px', borderRadius: '50%',
                                  background: corEstado, flexShrink: 0,
                                }} title={estadoCfg.label} />
                              )}
                            </div>
                          ) : (
                            <>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px', marginBottom: '1px' }}>
                                <span style={{ fontWeight: '700', fontSize: '12px', color: cor, flexShrink: 0 }}>
                                  {format(new Date(ag.data_hora_inicio), 'HH:mm')}
                                </span>
                                <span style={{
                                  fontWeight: '600', fontSize: '12px', color: 'var(--cor-texto)',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                                }}>
                                  {ag.cliente_nome || 'Sem nome'}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <span style={{
                                  fontSize: '11px', color: 'var(--cor-texto-suave)',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                                }}>
                                  {ag.servicos?.nome}
                                </span>
                                <span style={{
                                  fontSize: '10px', fontWeight: '700',
                                  color: corEstado, whiteSpace: 'nowrap', flexShrink: 0,
                                }}>
                                  {estadoCfg.label}
                                </span>
                              </div>
                              {height >= 80 && ag.cliente_telefone && (
                                <div style={{ fontSize: '10px', color: 'var(--cor-texto-suave)', marginTop: '1px' }}>
                                  {ag.cliente_telefone}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>

            {/* Estado vazio — sem profissionais */}
            {profissionaisVisiveis.length === 0 && (
              <div style={{
                textAlign: 'center', padding: '48px 24px', color: 'var(--cor-texto-suave)',
              }}>
                <p>Seleciona pelo menos um profissional.</p>
              </div>
            )}
            </div>

          </div>

          {/* Legenda do horário — mais compacta em ecrãs baixos/estreitos
              (telemóvel, incluindo em paisagem: usa "chromeCompacto", não
              "isMobile", porque a largura sozinha não apanha a paisagem) —
              só o essencial em texto; os blocos de bloqueio ficam só com a
              cor + tooltip, para dar o máximo de altura possível à grelha. */}
          <div ref={legendaRef} style={{
            display: 'flex', gap: chromeCompacto ? '10px' : '16px', flexWrap: 'wrap', alignItems: 'center',
            padding: chromeCompacto ? '5px 10px' : '8px 16px',
            borderTop: '1px solid var(--cor-borda)',
            background: 'var(--cor-creme)',
            fontSize: '11px', color: 'var(--cor-texto-suave)',
          }}>
            <span>
              <strong style={{ color: 'var(--cor-texto)' }}>Horário:</strong>{' '}
              {minutosParaHora(aberturaMin)} – {minutosParaHora(fechoMin)}
            </span>
            {almocoAtivoMin && !chromeCompacto && (
              <span>
                <strong style={{ color: 'var(--cor-texto)' }}>Almoço:</strong>{' '}
                {minutosParaHora(almocoInicioMin)} – {minutosParaHora(almocoFimMin)}
              </span>
            )}
            {agoraDentroHorario && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }} title="Hora atual">
                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#DC2626', flexShrink: 0 }} />
                {!chromeCompacto && 'Hora atual'}
              </span>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }} title="Bloqueio profissional">
              <span style={{ display: 'inline-block', width: '14px', height: '8px', borderRadius: '2px', background: 'repeating-linear-gradient(135deg, #FEE2E2, #FEE2E2 3px, #FECACA 3px, #FECACA 6px)', border: '1px solid #F87171', flexShrink: 0 }} />
              {!chromeCompacto && 'Bloqueio profissional'}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }} title="Bloqueio global">
              <span style={{ display: 'inline-block', width: '14px', height: '8px', borderRadius: '2px', background: 'repeating-linear-gradient(135deg, #FEF3C7, #FEF3C7 3px, #FDE68A 3px, #FDE68A 6px)', border: '1px solid #F59E0B', flexShrink: 0 }} />
              {!chromeCompacto && 'Bloqueio global'}
            </span>
          </div>
        </div>
      )}

      {slotBloqueio && (
        <ModalBloqueioRapido
          tenantId={tenant.id}
          data={data}
          prof={slotBloqueio.prof}
          horaInicioInicial={minutosParaHora(slotBloqueio.inicioMin)}
          horaFimInicial={minutosParaHora(
            slotBloqueio.fimMin != null
              ? fimTruncadoPorMarcacao(slotBloqueio.prof?.id, slotBloqueio.inicioMin, slotBloqueio.fimMin)
              : fimSugeridoMin(slotBloqueio.prof?.id, slotBloqueio.inicioMin)
          )}
          podeBloquearTodos={podeBloquearTodos}
          motivos={motivosBloqueio}
          duracaoMinima={duracaoMinimaBloqueio}
          onFechar={() => setSlotBloqueio(null)}
          onCriado={novo => {
            setBloqueios(prev => [...prev, novo].sort((a, b) => a.data_hora_inicio.localeCompare(b.data_hora_inicio)))
            setSlotBloqueio(null)
            toast.sucesso('Bloqueio criado.')
          }}
        />
      )}

      {bloqueioSelecionado && (
        <ModalEditarBloqueio
          tenantId={tenant.id}
          data={data}
          bloqueio={bloqueioSelecionado}
          prof={profissionais.find(p => p.id === bloqueioSelecionado.profissional_id) || null}
          podeBloquearTodos={podeBloquearTodos}
          motivos={motivosBloqueio}
          duracaoMinima={duracaoMinimaBloqueio}
          onFechar={() => setBloqueioSelecionado(null)}
          onAtualizado={atualizado => {
            setBloqueios(prev => prev.map(b => (b.id === atualizado.id ? { ...atualizado, profissionais: b.profissionais } : b)))
            setBloqueioSelecionado(null)
            toast.sucesso('Bloqueio atualizado.')
          }}
          onEliminado={id => {
            setBloqueios(prev => prev.filter(b => b.id !== id))
            setBloqueioSelecionado(null)
            toast.sucesso('Bloqueio eliminado.')
          }}
        />
      )}
    </main>
  )
}

// Modal de criação rápida de bloqueio, aberto ao clicar num horário da grelha
// em "modo bloqueio". Cobre só o mesmo dia da Agenda (para períodos de vários
// dias continua a existir a página Bloqueios, com o formulário completo).
function ModalBloqueioRapido({ tenantId, data, prof, horaInicioInicial, horaFimInicial, podeBloquearTodos, motivos, duracaoMinima, onFechar, onCriado }) {
  const toast = useToast()
  const [horaInicio, setHoraInicio] = useState(horaInicioInicial)
  const [horaFim, setHoraFim] = useState(horaFimInicial)
  const [motivo, setMotivo] = useState(motivos[0] || 'Bloqueado')
  const [cor, setCor] = useState(CORES_BLOQUEIO[0])
  const [espacoInteiro, setEspacoInteiro] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [conflitos, setConflitos] = useState(null)
  const [dadosPendentes, setDadosPendentes] = useState(null)

  const profissionalId = (podeBloquearTodos && espacoInteiro) ? null : (prof?.id || null)

  useEffect(() => {
    function aoTeclar(e) { if (e.key === 'Escape') onFechar() }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function submeter(e) {
    e.preventDefault()
    setConflitos(null)
    setDadosPendentes(null)

    if (horaParaMinutos(horaFim) <= horaParaMinutos(horaInicio)) {
      toast.erro('A hora de fim deve ser posterior à hora de início.')
      return
    }
    if (horaParaMinutos(horaFim) - horaParaMinutos(horaInicio) < duracaoMinima) {
      toast.erro(`O bloqueio tem de ter pelo menos ${duracaoMinima} minutos.`)
      return
    }
    const dados = {
      data_hora_inicio: new Date(`${data}T${horaInicio}:00`).toISOString(),
      data_hora_fim: new Date(`${data}T${horaFim}:00`).toISOString(),
      motivo: motivo.trim() || motivos[0] || 'Bloqueado',
      profissional_id: profissionalId,
      cor: cor || null,
    }

    setGuardando(true)
    try {
      const marcacoes = await getAgendamentosNoPeriodo(tenantId, dados.data_hora_inicio, dados.data_hora_fim, dados.profissional_id)
      if (marcacoes.length > 0) {
        setConflitos(marcacoes)
        setDadosPendentes(dados)
        setGuardando(false)
        return
      }
      await criar(dados)
    } catch (e) {
      toast.erro(e.message)
      setGuardando(false)
    }
  }

  async function criar(dados) {
    setGuardando(true)
    try {
      const novo = await createBloqueio(tenantId, dados)
      onCriado(novo)
    } catch (e) {
      toast.erro(e.message)
      setGuardando(false)
    }
  }

  return (
    <div
      role="presentation"
      onClick={e => { if (e.target === e.currentTarget) onFechar() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(44, 26, 14, 0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div style={{
        background: 'var(--cor-branco)', borderRadius: 'var(--raio-lg)',
        boxShadow: 'var(--sombra)', padding: '24px',
        width: 'min(420px, 100%)', maxHeight: '90vh', overflowY: 'auto',
      }}>
        <p style={{ fontWeight: '700', marginBottom: '4px' }}>Bloquear horário</p>
        <p style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', marginBottom: '18px' }}>
          {format(new Date(`${data}T12:00:00`), "d 'de' MMMM", { locale: pt })}
          {' · '}
          {profissionalId ? (prof?.nome || 'Profissional') : 'Espaço inteiro'}
        </p>

        {conflitos && conflitos.length > 0 ? (
          <div style={{
            background: '#FFFBEB', border: '1px solid #F4C77B', borderLeft: '4px solid var(--cor-ambar)',
            borderRadius: 'var(--raio)', padding: '14px 16px', marginBottom: '4px',
          }}>
            <p style={{ fontWeight: '700', color: '#92500E', marginBottom: '4px', fontSize: '14px' }}>
              ⚠️ Existem {conflitos.length} marca{conflitos.length === 1 ? 'ção' : 'ções'} neste período
            </p>
            <p style={{ fontSize: '13px', color: '#92500E', marginBottom: '12px' }}>
              O bloqueio não cancela estas marcações. Verifica-as antes de continuar:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
              {conflitos.map(m => (
                <div key={m.id} style={{
                  background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)',
                  borderRadius: 'var(--raio)', padding: '10px 12px',
                  display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                }}>
                  <span style={{ fontWeight: '700', color: 'var(--cor-principal)', fontSize: '13px', minWidth: '44px' }}>
                    {format(new Date(m.data_hora_inicio), 'HH:mm')}
                  </span>
                  <span style={{ flex: 1, minWidth: '120px', fontSize: '13px' }}>
                    <strong>{m.cliente_nome || 'Sem nome'}</strong>
                    {m.servicos?.nome && <span style={{ color: 'var(--cor-principal)' }}> · {m.servicos.nome}</span>}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button onClick={() => criar(dadosPendentes)} className="btn-primario" disabled={guardando} style={{ opacity: guardando ? 0.6 : 1, padding: '9px 18px', fontSize: '14px' }}>
                {guardando ? 'A guardar…' : 'Bloquear à mesma'}
              </button>
              <button onClick={() => { setConflitos(null); setDadosPendentes(null) }} className="btn-secundario" style={{ padding: '9px 18px', fontSize: '14px' }}>
                Voltar
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submeter}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginBottom: '14px' }}>
              <div style={{ marginRight: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Hora de início</label>
                <input type="time" value={horaInicio} onChange={e => setHoraInicio(e.target.value)} style={modalInputStyleHora} required />
              </div>
              <div style={{ marginRight: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Hora de fim</label>
                <input type="time" value={horaFim} onChange={e => setHoraFim(e.target.value)} style={modalInputStyleHora} required />
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Motivo</label>
              <SeletorMotivoBloqueio motivo={motivo} onChange={setMotivo} style={modalInputStyle} motivos={motivos} />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Cor na agenda</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {CORES_BLOQUEIO.map(c => {
                  const ativo = cor?.toLowerCase() === c.toLowerCase()
                  return (
                    <button
                      key={c} type="button" onClick={() => setCor(c)} title={c} aria-label={`Cor ${c}`}
                      style={{
                        width: '26px', height: '26px', borderRadius: '50%',
                        background: c, cursor: 'pointer', padding: 0,
                        border: ativo ? '3px solid var(--cor-texto)' : '2px solid var(--cor-borda)',
                      }}
                    />
                  )
                })}
              </div>
            </div>

            {podeBloquearTodos && prof && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={espacoInteiro} onChange={e => setEspacoInteiro(e.target.checked)} />
                Bloquear o espaço inteiro (todas as profissionais)
              </label>
            )}

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="submit" className="btn-primario" disabled={guardando} style={{ opacity: guardando ? 0.6 : 1 }}>
                {guardando ? 'A guardar…' : 'Criar Bloqueio'}
              </button>
              <button type="button" onClick={onFechar} className="btn-secundario">
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// Modal de edição/eliminação de um bloqueio já existente, aberto ao clicar
// num bloqueio na grelha em "modo bloqueio". Só cobre bloqueios do próprio
// dia da Agenda — os de vários dias continuam a editar-se na página
// Bloqueios (o clique nem chega a abrir este modal nesse caso).
function ModalEditarBloqueio({ tenantId, data, bloqueio, prof, podeBloquearTodos, motivos, duracaoMinima, onFechar, onAtualizado, onEliminado }) {
  const toast = useToast()
  const [horaInicio, setHoraInicio] = useState(() => format(new Date(bloqueio.data_hora_inicio), 'HH:mm'))
  const [horaFim, setHoraFim] = useState(() => format(new Date(bloqueio.data_hora_fim), 'HH:mm'))
  const [motivo, setMotivo] = useState(bloqueio.motivo || motivos[0] || 'Bloqueado')
  const [cor, setCor] = useState(bloqueio.cor || CORES_BLOQUEIO[0])
  const [espacoInteiro, setEspacoInteiro] = useState(bloqueio.profissional_id === null)
  const [guardando, setGuardando] = useState(false)
  const [eliminando, setEliminando] = useState(false)
  const [conflitos, setConflitos] = useState(null)
  const [dadosPendentes, setDadosPendentes] = useState(null)

  const profissionalId = (podeBloquearTodos && espacoInteiro) ? null : (prof?.id || null)

  useEffect(() => {
    function aoTeclar(e) { if (e.key === 'Escape') onFechar() }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function submeter(e) {
    e.preventDefault()
    setConflitos(null)
    setDadosPendentes(null)

    if (horaParaMinutos(horaFim) <= horaParaMinutos(horaInicio)) {
      toast.erro('A hora de fim deve ser posterior à hora de início.')
      return
    }
    if (horaParaMinutos(horaFim) - horaParaMinutos(horaInicio) < duracaoMinima) {
      toast.erro(`O bloqueio tem de ter pelo menos ${duracaoMinima} minutos.`)
      return
    }
    const dados = {
      data_hora_inicio: new Date(`${data}T${horaInicio}:00`).toISOString(),
      data_hora_fim: new Date(`${data}T${horaFim}:00`).toISOString(),
      motivo: motivo.trim() || motivos[0] || 'Bloqueado',
      profissional_id: profissionalId,
      cor: cor || null,
    }

    setGuardando(true)
    try {
      const marcacoes = await getAgendamentosNoPeriodo(tenantId, dados.data_hora_inicio, dados.data_hora_fim, dados.profissional_id)
      if (marcacoes.length > 0) {
        setConflitos(marcacoes)
        setDadosPendentes(dados)
        setGuardando(false)
        return
      }
      await guardar(dados)
    } catch (e) {
      toast.erro(e.message)
      setGuardando(false)
    }
  }

  async function guardar(dados) {
    setGuardando(true)
    try {
      const atualizado = await updateBloqueio(tenantId, bloqueio.id, dados)
      onAtualizado(atualizado)
    } catch (e) {
      toast.erro(e.message)
      setGuardando(false)
    }
  }

  async function eliminar() {
    if (!window.confirm('Eliminar este bloqueio?')) return
    setEliminando(true)
    try {
      await deleteBloqueio(tenantId, bloqueio.id)
      onEliminado(bloqueio.id)
    } catch (e) {
      toast.erro(e.message)
      setEliminando(false)
    }
  }

  return (
    <div
      role="presentation"
      onClick={e => { if (e.target === e.currentTarget) onFechar() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(44, 26, 14, 0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div style={{
        background: 'var(--cor-branco)', borderRadius: 'var(--raio-lg)',
        boxShadow: 'var(--sombra)', padding: '24px',
        width: 'min(420px, 100%)', maxHeight: '90vh', overflowY: 'auto',
      }}>
        <p style={{ fontWeight: '700', marginBottom: '4px' }}>Editar Bloqueio</p>
        <p style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', marginBottom: '18px' }}>
          {format(new Date(`${data}T12:00:00`), "d 'de' MMMM", { locale: pt })}
          {' · '}
          {profissionalId ? (prof?.nome || 'Profissional') : 'Espaço inteiro'}
        </p>

        {conflitos && conflitos.length > 0 ? (
          <div style={{
            background: '#FFFBEB', border: '1px solid #F4C77B', borderLeft: '4px solid var(--cor-ambar)',
            borderRadius: 'var(--raio)', padding: '14px 16px', marginBottom: '4px',
          }}>
            <p style={{ fontWeight: '700', color: '#92500E', marginBottom: '4px', fontSize: '14px' }}>
              ⚠️ Existem {conflitos.length} marca{conflitos.length === 1 ? 'ção' : 'ções'} neste período
            </p>
            <p style={{ fontSize: '13px', color: '#92500E', marginBottom: '12px' }}>
              O bloqueio não cancela estas marcações. Verifica-as antes de continuar:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
              {conflitos.map(m => (
                <div key={m.id} style={{
                  background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)',
                  borderRadius: 'var(--raio)', padding: '10px 12px',
                  display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                }}>
                  <span style={{ fontWeight: '700', color: 'var(--cor-principal)', fontSize: '13px', minWidth: '44px' }}>
                    {format(new Date(m.data_hora_inicio), 'HH:mm')}
                  </span>
                  <span style={{ flex: 1, minWidth: '120px', fontSize: '13px' }}>
                    <strong>{m.cliente_nome || 'Sem nome'}</strong>
                    {m.servicos?.nome && <span style={{ color: 'var(--cor-principal)' }}> · {m.servicos.nome}</span>}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button onClick={() => guardar(dadosPendentes)} className="btn-primario" disabled={guardando} style={{ opacity: guardando ? 0.6 : 1, padding: '9px 18px', fontSize: '14px' }}>
                {guardando ? 'A guardar…' : 'Guardar à mesma'}
              </button>
              <button onClick={() => { setConflitos(null); setDadosPendentes(null) }} className="btn-secundario" style={{ padding: '9px 18px', fontSize: '14px' }}>
                Voltar
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submeter}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginBottom: '14px' }}>
              <div style={{ marginRight: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Hora de início</label>
                <input type="time" value={horaInicio} onChange={e => setHoraInicio(e.target.value)} style={modalInputStyleHora} required />
              </div>
              <div style={{ marginRight: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Hora de fim</label>
                <input type="time" value={horaFim} onChange={e => setHoraFim(e.target.value)} style={modalInputStyleHora} required />
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Motivo</label>
              <SeletorMotivoBloqueio motivo={motivo} onChange={setMotivo} style={modalInputStyle} motivos={motivos} />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Cor na agenda</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {CORES_BLOQUEIO.map(c => {
                  const ativo = cor?.toLowerCase() === c.toLowerCase()
                  return (
                    <button
                      key={c} type="button" onClick={() => setCor(c)} title={c} aria-label={`Cor ${c}`}
                      style={{
                        width: '26px', height: '26px', borderRadius: '50%',
                        background: c, cursor: 'pointer', padding: 0,
                        border: ativo ? '3px solid var(--cor-texto)' : '2px solid var(--cor-borda)',
                      }}
                    />
                  )
                })}
              </div>
            </div>

            {podeBloquearTodos && prof && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={espacoInteiro} onChange={e => setEspacoInteiro(e.target.checked)} />
                Bloquear o espaço inteiro (todas as profissionais)
              </label>
            )}

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="submit" className="btn-primario" disabled={guardando || eliminando} style={{ padding: '9px 16px', fontSize: '14px', opacity: guardando ? 0.6 : 1 }}>
                {guardando ? 'A guardar…' : 'Guardar alterações'}
              </button>
              <button
                type="button" onClick={eliminar} disabled={guardando || eliminando}
                style={{
                  padding: '9px 16px', fontSize: '14px', borderRadius: 'var(--raio)',
                  border: '1px solid #DC2626', background: 'transparent', color: '#DC2626',
                  cursor: (guardando || eliminando) ? 'not-allowed' : 'pointer',
                  opacity: eliminando ? 0.6 : 1, fontFamily: 'var(--font-ui)',
                }}
              >
                {eliminando ? 'A eliminar…' : 'Eliminar'}
              </button>
              <button type="button" onClick={onFechar} className="btn-secundario" disabled={guardando || eliminando} style={{ padding: '9px 16px', fontSize: '14px' }}>
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

const modalInputStyle = {
  width: '100%', padding: '9px 12px',
  border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
  fontSize: '14px', fontFamily: 'var(--font-ui)',
  background: 'var(--cor-branco)', color: 'var(--cor-texto)',
  boxSizing: 'border-box',
}

// Os inputs de hora (type="time") desenham o valor/controlos nativos colados
// à borda direita, ignorando parte do padding normal — um pouco mais de
// espaço à direita evita que fiquem demasiado rentes à margem.
const modalInputStyleHora = { ...modalInputStyle, padding: '9px 14px 9px 12px' }
